<template>
  <div class="text-canvas-container">
    <canvas 
      ref="canvas"
      :width="width"
      :height="height"
      @click="handleClick"
    ></canvas>
  </div>
</template>

<script>
export default {
  name: 'TextCanvas',
  props: {
    content: {
      type: String,
      required: true
    },
    width: {
      type: Number,
      default: 300
    },
    height: {
      type: Number,
      default: 500
    },
    fontSize: {
      type: Number,
      default: 28
    },
    fontFamily: {
      type: String,
      default: 'var(--system-font)'
    },
    lineHeight: {
      type: Number,
      default: 1.5
    },
    color: {
      type: String,
      default: 'red'
    },
    backgroundColor: {
      type: String,
      default: 'lightgray'
    }
  },
  watch: {
    content: {
      handler: 'drawText',
      immediate: true
    },
    fontSize: {
      handler: 'drawText',
      immediate: true
    }
  },
  mounted() {
    this.drawText()
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    handleResize() {
      this.$nextTick(() => {
        this.drawText()
      })
    },
    drawText() {
      const canvas = this.$refs.canvas
      if (!canvas) return

      const dpr = window.devicePixelRatio || 1
      const rect = canvas.getBoundingClientRect()
      
      // Set physical canvas size
      canvas.width = rect.width * dpr
      canvas.height = rect.height * dpr
      
      const ctx = canvas.getContext('2d')
      
      // Clear everything first
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      
      // Set background if needed
      if (this.backgroundColor !== 'transparent') {
        ctx.fillStyle = this.backgroundColor
        ctx.fillRect(0, 0, canvas.width, canvas.height)
      }

      // Reset transform and set scale
      ctx.setTransform(1, 0, 0, 1, 0, 0)
      ctx.scale(dpr, dpr)

      // Set text properties AFTER scaling
      ctx.textAlign = 'left'
      ctx.textBaseline = 'top'
      ctx.fillStyle = this.color
      
      // Get computed font family from CSS variable
      const computedFontFamily = getComputedStyle(document.documentElement)
        .getPropertyValue('--system-font')
        .trim() || 'sans-serif'
      
      // Set font with weight, size and family
      const fontString = `${this.fontSize}px ${computedFontFamily}`
      ctx.font = fontString
      
      // Add error checking to verify font was set correctly
      if (ctx.font !== fontString) {
        console.warn('Font not set as expected:', {
          requested: fontString,
          actual: ctx.font
        })
      }

      const words = this.content.split(' ')
      const lineHeight = this.fontSize * this.lineHeight
      const maxWidth = rect.width - 20

      // Test text measurement
      const testText = "Test Text"
      const metrics = ctx.measureText(testText)
      console.log('Text metrics width:', metrics.width)

      let line = ''
      let y = 10 // Initial padding
      
      // Word wrapping
      for (let word of words) {
        const testLine = line + (line ? ' ' : '') + word
        const metrics = ctx.measureText(testLine)
        
        if (metrics.width > maxWidth && line !== '') {
          ctx.fillText(line, 10, y)
          line = word
          y += lineHeight
        } else {
          line = testLine
        }
      }
      
      // Draw remaining text
      if (line) {
        ctx.fillText(line, 10, y)
      }
    },
    handleClick() {
      this.$emit('click')
    }
  }
}
</script>

<style scoped>
.text-canvas-container {
  width: 100%;
  height: 100%;
  position: relative;
}

canvas {
  width: 100%;
  height: 100%;
}
</style> 